/**
 * Purchasing's tabs (UX-1, TASK-0240). Owner note 7 / C57: the order guide,
 * invoices, cost approvals and vendor catalogs are tabs inside Purchasing, under
 * the rail's "Buying" group. Step 1 keeps every URL as it was; UX-2 moves them
 * under /purchasing and adds "This week" first.
 */

export type PurchasingTabId =
  | 'guide'
  | 'orders'
  | 'invoices'
  | 'costs'
  | 'catalogs'
  | 'followups'
  | 'freight';

export interface PurchasingTab {
  id: PurchasingTabId;
  label: string;
  to: string;
  /** The second key of the `g` jump that opens this tab while in Purchasing. */
  chord: string;
}

export const PURCHASING_TABS: readonly PurchasingTab[] = [
  { id: 'guide', label: 'Order guide', to: '/purchasing/guide', chord: 'g' },
  { id: 'orders', label: 'Orders', to: '/purchasing', chord: 'o' },
  { id: 'invoices', label: 'Invoices', to: '/purchasing/import', chord: 'i' },
  { id: 'costs', label: 'Costs', to: '/vendors/cost-approvals', chord: 'c' },
  {
    id: 'catalogs',
    label: 'Catalogs & price lists',
    to: '/vendors/catalogs',
    chord: 'l',
  },
  // TASK-0247 (INC-3): what needs a second look (over-receipts, late orders, credits to chase…).
  { id: 'followups', label: 'Follow-ups', to: '/purchasing/follow-ups', chord: 'f' },
  // TASK-0257 (DROP-2a): trips from the drop-off locations and their freight.
  { id: 'freight', label: 'Freight', to: '/purchasing/freight', chord: 'r' },
];

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * The tab a path belongs to, or null outside Purchasing. Every other page under
 * /purchasing (a PO, New PO, Record invoice) belongs to Orders.
 */
export function purchasingTabFor(pathname: string): PurchasingTabId | null {
  if (under(pathname, '/purchasing/guide')) return 'guide';
  if (under(pathname, '/purchasing/import')) return 'invoices';
  if (under(pathname, '/vendors/cost-approvals')) return 'costs';
  if (under(pathname, '/vendors/catalogs')) return 'catalogs';
  if (under(pathname, '/purchasing/follow-ups')) return 'followups';
  if (under(pathname, '/purchasing/freight')) return 'freight';
  if (under(pathname, '/purchasing')) return 'orders';
  return null;
}

/** The tab a `g` chord opens while the operator is in Purchasing. */
export function purchasingChord(pathname: string, key: string): PurchasingTab | null {
  if (purchasingTabFor(pathname) === null) return null;
  return PURCHASING_TABS.find((tab) => tab.chord === key) ?? null;
}
